// TypeScript mirrors of the API contracts in apps/api/src/contracts.ts.
// If you change a contract there, change it here too.

export const STAGES = ['Lead', 'Opportunity', 'Tender', 'Won', 'Lost'] as const
export type Stage = (typeof STAGES)[number]

export const FUNDING_STATUSES = ['Approved', 'NeedToFind', 'NotSure', 'NotApplicable'] as const
export type FundingStatus = (typeof FUNDING_STATUSES)[number]

export const ACTIVITY_TYPES = ['Call', 'Meeting', 'Email', 'SiteVisit', 'Comment', 'Update', 'Note', 'Other'] as const
export type ActivityType = (typeof ACTIVITY_TYPES)[number]

export type DepartmentType = 'State' | 'Central' | 'Municipal' | 'Other'

export const DOCUMENT_CATEGORIES = [
  'TenderDocument',
  'Proposal',
  'Correspondence',
  'Contract',
  'MeetingNotes',
  'Other',
] as const
export type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[number]

/** 10 MB, the same limit the server enforces. */
export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024

export type DocumentItem = {
  id: number
  projectId: number | null
  /** Set when the file was attached while logging an activity. */
  activityId: number | null
  fileName: string
  contentType: string
  sizeBytes: number
  category: DocumentCategory
  uploadedByName: string
  uploadedAt: string
  isArchived: boolean
}

/** Dates without a time arrive as "2026-10-05"; timestamps as ISO strings. */
export type DateOnly = string

export type User = {
  id: number
  name: string
  email: string
  initials: string
  isAdmin: boolean
  isActive: boolean
  /** True after an account is created or reset: they must choose their own password first. */
  mustChangePassword: boolean
}

/** Who is signed in, and whether this server allows the sample/demo data tools. */
export type Session = { user: User; demoTools: boolean }

export type TemporaryPassword = { user: User; temporaryPassword: string }

export const DEPARTMENT_TYPES: DepartmentType[] = ['State', 'Central', 'Municipal', 'Other']

export type Department = {
  id: number
  name: string
  type: DepartmentType
  isArchived: boolean
  /** Projects and stakeholders (not archived) that use this department. */
  projectCount: number
  stakeholderCount: number
}

export type DepartmentInput = { name: string; type: DepartmentType }

export type LinkedProject = {
  id: number
  title: string
  departmentName: string
  valueRupees: number
  stage: Stage
  isArchived: boolean
}

export type Interaction = {
  activityId: number
  date: DateOnly
  type: ActivityType
  note: string
  loggedByName: string
  projectId: number
  projectTitle: string
}

export type StakeholderDetail = {
  stakeholder: StakeholderItem
  projects: LinkedProject[]
  interactions: Interaction[]
}

export type DocumentListItem = { document: DocumentItem; projectTitle: string | null }

export type StakeholderItem = {
  id: number
  name: string
  role: string | null
  /** What to show: the department's name, or the free-text organisation. */
  organisation: string | null
  departmentId: number | null
  isArchived: boolean
  phone: string | null
  email: string | null
  notes: string | null
  /** Linked projects that are not archived. */
  projectCount: number
}

export type StakeholderInput = {
  name: string
  departmentId: number | null
  organisation: string | null
  role: string | null
  phone: string | null
  email: string | null
  notes: string | null
}

export type Options = {
  states: string[]
  categories: string[]
}

export type ProjectListItem = {
  id: number
  title: string
  departmentId: number
  departmentName: string
  stage: Stage
  state: string | null
  category: string | null
  valueRupees: number
  fundingStatus: FundingStatus
  nextFollowUp: DateOnly | null
  isOverdue: boolean
  daysOverdue: number
  triggerSource: string | null
  notes: string | null
  isStarred: boolean
  isArchived: boolean
  createdById: number
  createdByName: string
  /** The person leading the proposal (starts as the creator; admins can reassign). */
  ownerId: number
  ownerName: string
  createdAt: string
  updatedAt: string
  stakeholderNames: string[]
  /** The same people as stakeholderNames, with ids for linking. */
  stakeholders: StakeholderRef[]
  /** Date of the most recent logged activity, if any. */
  lastActivityDate: DateOnly | null
}

export type StakeholderRef = {
  id: number
  name: string
  role: string | null
}

export type ActivityItem = {
  id: number
  date: DateOnly
  type: ActivityType
  note: string
  loggedByName: string
  createdAt: string
  stakeholders: StakeholderRef[]
  documents: DocumentItem[]
}

/** One line in the Dashboard's "Recent activity" feed. */
export type RecentActivity = {
  id: number
  date: DateOnly
  type: ActivityType
  note: string
  loggedByName: string
  projectId: number
  projectTitle: string
}

export type ProjectDetail = {
  summary: ProjectListItem
  stakeholders: StakeholderRef[]
  activities: ActivityItem[]
  documents: DocumentItem[]
  /** What the signed-in user may change on this project. */
  access: ProjectAccess
}

/**
 * Edit rules, decided by the server (apps/api/src/permissions.ts):
 * identity fields lock 24 hours after saving (owner/admins can correct with a reason),
 * Won/Lost projects are frozen except for their owner and admins.
 */
export type ProjectAccess = {
  /** Title, department, state, category, trigger source. */
  canEditIdentity: boolean
  /** An identity change must come with a reason (the creator's 24 hours have passed). */
  identityNeedsReason: boolean
  /** When the creator's free-correction window closes; null when it does not apply. */
  graceEndsAt: string | null
  /** Stage, value, funding, follow-up date, adding stakeholders. */
  canEditProgress: boolean
  canRemoveStakeholders: boolean
  canReopen: boolean
  canReassignOwner: boolean
  isClosed: boolean
  isOwner: boolean
}

/** The fields that lock after saving. */
export const IDENTITY_FIELDS = ['title', 'departmentId', 'state', 'category', 'triggerSource'] as const

export type ProjectInput = {
  title: string
  departmentId: number
  stage: Stage
  state: string | null
  category: string | null
  valueCrore: number
  fundingStatus: FundingStatus
  nextFollowUp: DateOnly | null
  triggerSource: string | null
  notes: string | null
  stakeholderIds: number[]
  /** Who leads the proposal (admins only may change it). */
  ownerId?: number | null
  /** Why a locked field is being corrected (required after the 24-hour window). */
  reason?: string | null
}

export type ActivityInput = {
  date: DateOnly
  type: ActivityType
  note: string
  stakeholderIds: number[]
  nextFollowUp: DateOnly | null
}
